import React, { useCallback, useEffect, useState } from "react";
import { IonContent, IonButton, IonIcon } from "@ionic/react";
import { chevronBack, expandOutline, settingsOutline } from "ionicons/icons";
import {
  SetupClear1Image,
  SetupClear2Image,
  SetupClear3Image,
  SetupCough1Image,
  SetupCough2Image,
  SetupCough3Image,
  SetupCough4Image,
} from "../../../assets/icons/index";
import analytics from "../../../services/firebaseAnalytics";

interface QrScanProps {
  usesBiWazeDevice: string | null;
  currentStep: number;
  steps: any[];
  hideSkip?: boolean;
  prevStep: () => void;
  nextStep: (step?: number) => void;
  handleSkipSetup?: () => void;
  handleComplete: () => void;
  devices?: any[];
}

// Types
interface SetupStep {
  stepNumber: string;
  title: React.ReactNode;
  subtitle?: string;
  image: string;
}
const QrScan: React.FC<QrScanProps> = ({
  usesBiWazeDevice,
  currentStep,
  steps,
  prevStep,
  nextStep,
  hideSkip = false,
  handleSkipSetup,
  handleComplete,
  devices = [],
}) => {
  const [scannedContent, setScannedContent] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [setupSteps, setSetupSteps] = useState<SetupStep[]>([]);

  const generateSetupSteps = useCallback((): SetupStep[] => {
    const isCoughDevice = usesBiWazeDevice?.includes("Cough");
    if (isCoughDevice) {
      return [
        {
          stepNumber: "1",
          title: (
            <>
              Press the{" "}
              <IonIcon
                icon={settingsOutline}
                style={{
                  fontSize: "18px",
                  verticalAlign: "middle",
                  margin: "0 4px",
                }}
              />{" "}
              icon
            </>
          ),
          subtitle: "",
          image: SetupCough1Image,
        },
        {
          stepNumber: "2",
          title: "Press Device Settings",
          image: SetupCough2Image,
        },
        {
          stepNumber: "3",
          title: "Press Arc Connect",
          image: SetupCough3Image,
        },
        {
          stepNumber: "4",
          title: "Tap Scan the QR code",
          image: SetupCough4Image,
        },
      ];
    }

    return [
      {
        stepNumber: "1",
        title: (
          <>
            Press the{" "}
            <IonIcon
              icon={settingsOutline}
              style={{
                fontSize: "18px",
                verticalAlign: "middle",
                margin: "0 4px",
              }}
            />{" "}
            icon
          </>
        ),
        subtitle: "",
        image: SetupClear1Image,
      },
      {
        stepNumber: "2",
        title: "Press Arc Connect",
        image: SetupClear2Image,
      },
      {
        stepNumber: "3",
        title: "Tap Scan the QR code",
        image: SetupClear3Image,
      },
    ];
  }, [usesBiWazeDevice]);

  useEffect(() => {
    if (!usesBiWazeDevice) prevStep();
    if (usesBiWazeDevice && usesBiWazeDevice.length > 0) {
      setSetupSteps(generateSetupSteps());
    }
    const logAnalyticsEvent = async () => {
      await analytics.logEvent("onboarding_device_instruction_viewed", {
        usesBiWazeDevice: usesBiWazeDevice ? usesBiWazeDevice.toString() : "No",
      });
    };
    logAnalyticsEvent();
  }, [usesBiWazeDevice, generateSetupSteps]);

  async function startScan() {
    try {
      const result: any = null;
      if (result?.hasContent) {
        console.log("Scanned content:", result.content);
        setScannedContent(result.content);
        // Handle the scanned QR code content here
      } else {
        console.log("Scan cancelled or no content found");
      }
    } catch (error) {
      console.error("Error scanning QR code:", error);
    }
  }

  const handleImageClick = (image: string) => {
    setFullScreenImage(image);
  };

  return (
    <IonContent className="ion-padding onboarding-section">
      <div className="navigation-header">
        <button className="back-text-button" onClick={prevStep}>
          <IonIcon icon={chevronBack} className="back-icon" />
          <span>Back</span>
        </button>
        {/* <button className="skip-setup-button" onClick={handleSkipSetup}>
          Skip Setup
        </button> */}
      </div>

      <div className="onboarding-section !p-0">
        <h1
          className="!mb-4"
          style={{
            fontSize: "28px",
            textAlign: "center",
            fontWeight: "bold",
            color: "#333",
            marginBottom: "32px",
          }}
        >
          Turn on the {usesBiWazeDevice} and follow the steps below
        </h1>

        {/* Steps Container */}
        <div
          className="steps-container col-span-12"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridAutoRows: "minmax(140px, auto)",
            gap: "12px",
          }}
        >
          {setupSteps.map((step, index) => (
            <div
              key={index}
              className="step-item col-span-1"
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f8f9fa",
                borderRadius: "12px",
                padding: "10px",
                position: "relative",
                minHeight: "140px",
              }}
            >
              {/* Step Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <div
                    style={{
                      backgroundColor: "#17a2b8",
                      color: "white",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      marginRight: "12px",
                      flexShrink: 0,
                    }}
                  >
                    {step.stepNumber}
                  </div>
                  <div>
                    <h3
                      style={{
                        // fontSize: "16px",
                        // fontWeight: "600",
                        margin: "0",
                        color: "#333",
                        // lineHeight: "1.4",
                      }}
                    >
                      {step.title}
                    </h3>
                    {step.subtitle && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#666",
                          margin: "4px 0 0 0",
                          lineHeight: "1.4",
                        }}
                      >
                        {step.subtitle}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleImageClick(step.image)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px",
                    marginLeft: "8px",
                  }}
                  aria-label="Expand image"
                >
                  <IonIcon
                    icon={expandOutline}
                    style={{
                      fontSize: "18px",
                      color: "#666",
                    }}
                  />
                </button>
              </div>

              {/* Step Image - thumbnail */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  // backgroundColor: "#fff",
                  // borderRadius: "8px",
                  // padding: "6px",
                  // border: "1px solid #e9ecef",
                  flex: 1,
                  alignItems: "center",
                }}
              >
                <img
                  src={step.image}
                  alt={`Step ${step.stepNumber}`}
                  style={{
                    // width: "80px",
                    height: "56px",
                    objectFit: "cover",
                    cursor: "pointer",
                    borderRadius: "4px",
                  }}
                  onClick={() => handleImageClick(step.image)}
                  draggable={false}
                />
              </div>
            </div>
          ))}
          <div className="items-center text-center col-span-2">
            Press the button below to scan the QR Code
          </div>
        </div>

        {scannedContent && (
          <div
            className="scanned-content"
            style={{
              backgroundColor: "#d4edda",
              border: "1px solid #c3e6cb",
              borderRadius: "8px",
              // padding: "16px",
              marginBottom: "24px",
            }}
          >
            <p style={{ margin: "0", color: "#155724" }}>
              Scanned: {scannedContent}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="button-stack-container">
          <IonButton
            expand="block"
            className="primary-button"
            onClick={() => nextStep()}
          >
            Scan the QR code
          </IonButton>
          {!hideSkip && (
            <button
              className="skip-link"
              onClick={() =>
                handleSkipSetup
                  ? handleSkipSetup()
                  : nextStep(devices.length > 1 ? 8 : 7)
              }
            >
              Skip for now
            </button>
          )}
        </div>
      </div>

      {/* Full screen image modal */}
      {fullScreenImage && (
        <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50">
          {/* Image container with close button */}
          <div className="relative inline-block">
            {/* Close button positioned near the image */}
            <button
              onClick={() => setFullScreenImage(null)}
              className="absolute -top-16 -right-2.5 bg-black bg-opacity-70 border-none rounded-full text-white text-2xl cursor-pointer w-10 h-10 flex items-center justify-center z-50 shadow-lg hover:bg-opacity-90 transition-all"
              aria-label="Close full screen"
            >
              &times;
            </button>

            <img
              src={fullScreenImage}
              alt="Full screen"
              className="max-w-[90vw] max-h-[90vh] rounded-xl shadow-2xl"
              // Touch events for swipe
              onTouchStart={(e) => {
                (window as any)._touchStartX = e.touches[0].clientX;
              }}
              onTouchEnd={(e) => {
                const startX = (window as any)._touchStartX;
                const endX = e.changedTouches[0].clientX;
                if (startX !== undefined) {
                  const diff = endX - startX;
                  if (Math.abs(diff) > 60) {
                    const currentIndex = setupSteps.findIndex(
                      (s) => s.image === fullScreenImage
                    );
                    if (diff < 0 && currentIndex < setupSteps.length - 1) {
                      setFullScreenImage(setupSteps[currentIndex + 1].image);
                    } else if (diff > 0 && currentIndex > 0) {
                      setFullScreenImage(setupSteps[currentIndex - 1].image);
                    }
                  }
                  (window as any)._touchStartX = undefined;
                }
              }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </IonContent>
  );
};

export default QrScan;
